'use client';
// My Wallet — the active session wallet only.
// Shows the primary AMAN balance up front; every other asset lives behind a
// collapsible "Details" list (avatar · symbol · balance rows — never duplicated).
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Wallet, KeyRound, ShieldCheck,
  Coins, History, Atom, Star, ChevronDown, Layers, Lock,
} from 'lucide-react';
import {
  fetchJson, usePoll, post, fmtAmt, fmtInt,
  SectionHeader, AddressChip, TypeBadge, Mono, CopyBtn, QuantumBadge, TokenAvatar,
} from './shared';
import { useSession, ConnectPrompt } from './session';
import type { Net, AddressInfo } from './types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export function Wallets({ net }: { net: Net }) {
  const { session, openAccount } = useSession();

  if (!session) {
    return (
      <div className="space-y-5">
        <SectionHeader
          icon={Wallet}
          title="My wallet — hybrid & quantum-protected"
          desc="One seed derives two keypairs: classic Ed25519 + quantum ML-DSA-87 (NIST FIPS 204) — encrypted at rest with AES-256-GCM"
        />
        <ConnectPrompt what="view your wallet" />
      </div>
    );
  }

  return <MyWallet net={net} session={session} openAccount={openAccount} />;
}

function MyWallet({ net, session, openAccount }: {
  net: Net; session: { walletId: string; name: string; address: string; kind?: string }; openAccount: () => void;
}) {
  const { data } = usePoll<{ address: AddressInfo }>(
    async () => fetchJson(`/api/explorer?net=${net}&what=address&q=${session.address}`),
    4000, [net, session.address],
  );
  const [detailsOpen, setDetailsOpen] = useState(false);
  const info = data?.address;
  const address = session.address;

  const tokens = (info?.assets ?? []).filter(a => a.balance > 1e-9);

  return (
    <div className="space-y-5">
      <SectionHeader
        icon={Wallet}
        title={session.name}
        desc="Your session wallet — every action in the app (mining, send, swap, deploy) runs under this address only"
        action={
          <Button variant="secondary" onClick={openAccount} className="gap-2 text-xs font-bold">
            <Lock className="size-3.5" /> Switch / lock
          </Button>
        }
      />

      {/* Primary card — one coin up front */}
      <div className="glass p-6 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" aria-hidden />
        <div className="relative space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="rounded-xl bg-primary/10 text-primary ring-1 ring-primary/25 p-2.5"><Wallet className="size-5" /></span>
              <div>
                <div className="font-black flex items-center gap-2">
                  {session.name}
                  {session.kind === 'founder' && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/30 px-2 py-0.5 text-[0.6rem] font-black" title="This wallet launched the network">
                      <Star className="size-3" /> FOUNDER
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 mt-1"><AddressChip address={address} /><CopyBtn text={address} /></div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <QuantumBadge />
              <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground ring-1 ring-border px-2 py-0.5 text-[0.62rem] font-semibold" title="Dual shield: Ed25519 + ML-DSA-87">
                <Atom className="size-3" /> Dual shield
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-8">
            <div>
              <div className="text-[0.65rem] text-muted-foreground flex items-center gap-1"><Coins className="size-3" /> Primary balance</div>
              <div className="text-4xl font-black text-emerald-400 mt-1" dir="ltr">{fmtAmt(info?.balance ?? 0)}</div>
              <div className="text-[0.68rem] text-muted-foreground">AMAN available</div>
            </div>
            {(info?.staked ?? 0) > 0 && (
              <div>
                <div className="text-[0.65rem] text-muted-foreground">Staked</div>
                <div className="text-2xl font-black text-teal-400 mt-1" dir="ltr">{fmtAmt(info?.staked ?? 0)}</div>
                <div className="text-[0.68rem] text-muted-foreground">AMAN earning rewards</div>
              </div>
            )}
          </div>

          {/* Assets beyond the primary coin live behind Details */}
          <div className="rounded-xl ring-1 ring-border bg-muted/30 overflow-hidden">
            <button
              onClick={() => setDetailsOpen(o => !o)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition"
              aria-expanded={detailsOpen}
            >
              <span className="text-xs font-bold flex items-center gap-2">
                <Layers className="size-4 text-violet-400" /> Details — all assets ({tokens.length} token{tokens.length === 1 ? '' : 's'})
              </span>
              <ChevronDown className={`size-4 text-muted-foreground transition-transform ${detailsOpen ? 'rotate-180' : ''}`} />
            </button>
            {detailsOpen && (
              <div className="border-t border-border/60 divide-y divide-border/40">
                {tokens.length === 0 && (
                  <div className="px-4 py-5 text-xs text-muted-foreground text-center">
                    No tokens yet — buy on the DEX, deploy in the Token Lab, or receive a transfer.
                  </div>
                )}
                {tokens.map(a => (
                  <div key={a.symbol} className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/40 transition">
                    <span className="flex items-center gap-2.5 min-w-0">
                      <TokenAvatar symbol={a.symbol} logo={a.info?.logo} size={24} />
                      <span className="min-w-0">
                        <span className="text-xs font-black block truncate">{a.info?.name ?? a.symbol}</span>
                        <span className="text-[0.6rem] text-muted-foreground">
                          {a.symbol}
                          {a.info?.stable && a.info?.pegAman ? ` · stable 1 = ${fmtAmt(a.info.pegAman, 4)} AMAN` : ''}
                        </span>
                      </span>
                    </span>
                    <span className="text-sm font-black shrink-0" dir="ltr">{fmtAmt(a.balance)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <WalletDetail net={net} address={address} />
    </div>
  );
}

// ---------- History + seed export for the active wallet ----------
function WalletDetail({ net, address }: { net: Net; address: string }) {
  const { data } = usePoll<{ address: AddressInfo }>(
    async () => fetchJson(`/api/explorer?net=${net}&what=address&q=${address}`),
    5000, [net, address],
  );
  const info = data?.address;
  const [exportOpen, setExportOpen] = useState(false);

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 glass p-5">
        <div className="text-sm font-bold mb-3 flex items-center gap-1.5"><History className="size-4" /> Transaction history</div>
        <div className="space-y-1.5 max-h-96 overflow-y-auto pl-1">
          {(info?.history ?? []).map(t => (
            <div key={t.id + String(t.height)} className="rounded-md border border-border/60 bg-muted/30 p-2.5">
              <div className="flex items-center justify-between">
                <TypeBadge type={t.type} label={t.label ?? t.type} />
                <span className="text-[0.85rem] font-bold" dir="ltr" style={{ color: t.from === address ? '#f87171' : '#34d399' }}>
                  {t.from === address ? '−' : '+'}{fmtAmt(t.amount)} {t.type.startsWith('token') ? t.data?.symbol : 'AMAN'}
                </span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <Mono>{t.id.slice(0, 18)}…</Mono>
                <span className="text-[0.62rem] text-muted-foreground">
                  {t.pending ? 'pending' : t.height !== null && t.height !== undefined ? `block #${fmtInt(t.height)}` : ''}
                </span>
              </div>
            </div>
          ))}
          {info && info.history.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">No transactions yet</div>}
        </div>
      </div>

      <div className="space-y-3">
        <div className="glass p-5">
          <div className="text-sm font-bold mb-2 flex items-center gap-1.5"><ShieldCheck className="size-4 text-emerald-400" /> Security</div>
          <ul className="text-[0.68rem] text-muted-foreground space-y-1.5 leading-relaxed list-disc pl-4">
            <li>Keys are encrypted with AES-256-GCM under your password — nothing is ever stored in the browser.</li>
            <li>Every transaction needs both signatures: Ed25519 and ML-DSA-87.</li>
            <li>The session lives in this tab only — closing the tab locks the wallet.</li>
          </ul>
          <Button variant="secondary" size="sm" className="mt-3 w-full gap-1.5 text-xs" onClick={() => setExportOpen(e => !e)}>
            <KeyRound className="size-3.5" /> {exportOpen ? 'Close export' : 'Export seed'}
          </Button>
        </div>
        {exportOpen && <ExportBox net={net} address={address} />}
      </div>
    </div>
  );
}

function ExportBox({ net, address }: { net: Net; address: string }) {
  const { data } = usePoll<{ wallets: Array<{ id: string; address: string }> }>(
    async () => fetchJson(`/api/wallets?net=${net}`), 60000, [net]);
  const walletId = data?.wallets.find(w => w.address === address)?.id;
  const [password, setPassword] = useState('');
  const [seed, setSeed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const doExport = async () => {
    if (!walletId) { toast.error('Wallet lookup failed'); return; }
    setBusy(true);
    try {
      const r = await post<{ seed: string; error?: string }>(`/api/wallets/export?net=${net}`, { id: walletId, password });
      if (r.error) { toast.error(r.error); return; }
      setSeed(r.seed);
      toast.warning('The seed is exposed now — store it and close');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="glass p-5 space-y-2">
      <div className="text-xs font-bold text-amber-400">Seed export requires the password — GCM auth rejects wrong ones</div>
      {!seed ? (
        <div className="flex gap-2">
          <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" />
          <Button size="sm" onClick={doExport} disabled={busy || !password}>Export</Button>
        </div>
      ) : (
        <div className="space-y-2">
          <Mono className="block text-amber-300 break-all text-[0.7rem] bg-black/40 p-2.5 rounded-md border border-amber-500/20">
            {revealed ? seed : '•'.repeat(64)}
          </Mono>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" className="text-xs" onClick={() => setRevealed(r => !r)}>{revealed ? 'Hide' : 'Reveal'}</Button>
            <Button size="sm" variant="secondary" className="text-xs" onClick={() => { navigator.clipboard.writeText(seed).catch(() => {}); toast.success('Copied'); }}>Copy</Button>
          </div>
        </div>
      )}
    </div>
  );
}
